import { useMemo } from 'react';
import {
  UseUploadStateProps,
  UseUploadStateReturn,
} from '../types';
import {
  areRequiredPhotosUploaded,
  canUploadMoreFiles,
} from '../utils/calculations';

/**
 * @description Custom hook for managing upload state calculations
 * @param filesCount - The number of files
 * @param seatRequirements - The seat requirements
 * @returns {
 *   requiredPhotosUploaded: boolean;
 *   canUploadMore: boolean;
 *   maxFiles: number;
 *   totalPhotosRequired: number | null;
 *   photosRemaining: number;
 * }
 */
export const useUploadState = ({
  filesCount,
  seatRequirements
}: UseUploadStateProps): UseUploadStateReturn => {

  const totalPhotosRequired = seatRequirements?.totalPhotosRequired || null;
  const maxFiles = totalPhotosRequired || 10;

  const requiredPhotosUploaded = useMemo(() => {
    return areRequiredPhotosUploaded(filesCount, totalPhotosRequired);
  }, [filesCount, totalPhotosRequired]);

  const canUploadMore = useMemo(() => {
    return canUploadMoreFiles(filesCount, totalPhotosRequired);
  }, [filesCount, totalPhotosRequired]);

  const photosRemaining = useMemo(() => {
    if (!totalPhotosRequired) return 0;
    return Math.max(0, totalPhotosRequired - filesCount);
  }, [totalPhotosRequired, filesCount]);

  return {
    requiredPhotosUploaded,
    canUploadMore,
    maxFiles,
    totalPhotosRequired,
    photosRemaining,
  };
};